import * as THREE from 'three';
import { BasketballCourt } from '../models/BasketballCourt';
import { BasketballDimensions } from '@shared/constants/BasketballDimensions';
import { eventBus } from '../events/EventBus';
import { Arena } from '../models/Arena';
import { Debug, LogCategory, LogLevel } from '../../../shared/Debug';

/**
 * Manages the basketball court and related elements
 */
export class CourtManager {
  private scene: THREE.Scene;
  private court: BasketballCourt | null = null;
  private arena: Arena | null = null;
  private hoopAPosition: THREE.Vector3 = new THREE.Vector3(0, BasketballDimensions.rim.heightAboveGround, -BasketballDimensions.court.length/2 + BasketballDimensions.hoopSupport.distanceFromBaseline);
  private hoopBPosition: THREE.Vector3 = new THREE.Vector3(0, BasketballDimensions.rim.heightAboveGround, BasketballDimensions.court.length/2 - BasketballDimensions.hoopSupport.distanceFromBaseline);

  // Background elements
  private backQuad: THREE.Mesh | null = null;
  private groundQuad: THREE.Mesh | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /**
   * Initialize the court and related elements
   */
  public initialize(): void {
    // Create the basketball court
    this.court = new BasketballCourt();
    this.scene.add(this.court.getMesh());
    this.court.getMesh().rotation.set(0, Math.PI/2, 0);
    
    // Create the arena
    this.arena = new Arena();
    this.scene.add(this.arena.getMesh());
    this.arena.getMesh().rotation.set(0, Math.PI/2, 0);
    

    // Calculate rim positions from court mesh
    this.hoopAPosition = this.court.getRimPosition('A');
    this.hoopBPosition = this.court.getRimPosition('B');
    
    Debug.info('Court initialized with rim positions:', LogCategory.COURT);
    
    // Publish event that court is ready
    eventBus.publish('court:initialized', {
      hoopAPosition: this.hoopAPosition,
      hoopBPosition: this.hoopBPosition
    });
  }


  /**
   * Get the court dimensions
   */
  public getCourtDimensions(): { width: number; length: number } | null {
    if (!this.court) return null;

    const dimensions = this.court.getDimensions();
    
    return {
      width: dimensions.length,
      length: dimensions.width
    };
  }

  /**
   * Get the home basket position
   */
  public getHoopAPosition(): THREE.Vector3 {
    return this.hoopAPosition;
  }
  
  /**
   * Get the away basket position
   */
  public getHoopBPosition(): THREE.Vector3 {
    return this.hoopBPosition;
  }

  /**
   * Update the court for the current frame
   */
  public update(deltaTime: number): void {
    // Update any animations if needed
    if (this.court) {
      const currentTime = Date.now();
      this.court.updateNetAnimations(currentTime/*, deltaTime*/);
    }
  }

  /**
   * Trigger net animation for a specific basket
   */
  public triggerNetAnimation(basketId: 'A' | 'B'): void {
    if (this.court) {
      this.court.triggerNetAnimation(basketId);
    }
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    if (this.court) {
      this.scene.remove(this.court.getMesh());
      this.court = null;
    }

    if (this.arena) {
      this.scene.remove(this.arena.getMesh());
      this.arena = null;
    }

    if (this.backQuad) {
      this.scene.remove(this.backQuad);
      this.backQuad = null;
    }

    if (this.groundQuad) {
      this.scene.remove(this.groundQuad);
      this.groundQuad = null;
    }
  }
}